import React from 'react';

import dayjs from "dayjs";

import {CircularProgress, Stack} from "@mui/joy";
import Typography from "@mui/joy/Typography";
import CardContent from "@mui/joy/CardContent";
import Card from "@mui/joy/Card";
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import Grid from "@mui/joy/Grid";


// components
import BaseApi from "../../utils/BaseApi";
import UserSubscriberContext from "../../context/UserSubscriberContext";
import CommonForm from "../Common/CommonForm";
import FinancialModelPeriodExpenseCreate from "./FinancialModelPeriodExpenseCreate";
import FinancialModelOnceExpenseCreate from "./FinancialModelOnceExpenseCreate";
import {compareDates, getDatesInRange} from "../../utils/misc";
import CommonPaginatedTable from "../Common/CommonPaginatedTable";
import FinancialModelOpportunityCardWrapper from "./FinancialModelOpportunityCardWrapper";
import FinancialModelSalaryCreate from "./FinancialModelSalaryCreate";
import FinancialModelConfigValueUpdate from "./FinancialModelConfigValueUpdate";
import FinancialModelForecastParameters from "./FinancialModelForecastParameters";
import FinancialTotals from "./FinancialTotals";
import {calculateTotals,} from "./utils";
import withRouter from "../../utils/withRouter";
import CommonCallBackButton from "../Common/CommonCallBackButton";

// icons
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from '@mui/icons-material/Refresh';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import EditIcon from '@mui/icons-material/Edit';

dayjs.locale('ru');


class FinancialModel extends React.Component {
    static contextType = UserSubscriberContext

    constructor(props) {
        super(props);
        this.state = {
            opportunities: [],
            forecast_revenues: [],
            forecast_incomes: [],
            payments: null,
            certificates: null,
            statuses: ['OPENX',],
            xtypes_choices: null,
            xtypes: [],
            needRefresh: false,
            ready: false,
            config: null,
            report: null,
            total: null,
            quarters: null,
            title: null,
        }
        this.year = new Date().getFullYear()
    }

    async componentDidMount() {
        await this.retrieveConfig();
        await this.retrievePayments();
        await this.retrieveCertificates();
        await this.retrieveXtypes();
        await this.calculateReport();
    }

    async sanitizeConfig(config){
        for (let key of ['onceExpenses', 'periodExpenses', 'salaries', 'adminSalaries']){
            if (!config[key]){
                config[key] = {}
            }
        }
        if (!config.startCash){
                config.startCash = 0
            }
        await this.postConfig(config);
        return config
    }

    async retrieveXtypes() {
        const api = new BaseApi();
        let url = 'attachment/subscriber_xtypes/';
        let response = await api.get(
            url,
            {},
        );
        let xtypes_choices = [];
        for (const category of response.data.all_xtypes) {
            if (response.data.available_xtypes.includes(category[0])) {
                xtypes_choices.push(category);
            }
        }
        this.setState({
            xtypes_choices: xtypes_choices,
        });
    }

    async postConfig(config) {
        const url = `financial_model/${this.props.params.modelId}/`;
        const api = new BaseApi();
        await api.patch(
            url,
            {
                config: config
            }
        );
    }

    async retrieveConfig() {
        const url = `financial_model/${this.props.params.modelId}`;
        const api = new BaseApi();
        let response = await api.get(
            url,
            {}
        );
        let config = response.data.config;
        config = await this.sanitizeConfig(config);
        this.setState({
            config: config,
            title: response.data.title,
        });
    }

    setOpportunityValue(value){
        let opportunities = this.state.opportunities;
        for (let opportunity of opportunities){
            if (opportunity.opportunity.id === value.id){
                opportunity.opportunity = value
            }
        }
        this.setState({
            opportunities: opportunities,
            needRefresh: true,
        })
    }

    async retrieveOpportunities() {
        const url = 'opportunity/report/';
        const api = new BaseApi();
        let params = {
            xtypes: this.state.xtypes.join(','),
            cards: true,
        };
        let opportunities = [];
        for (let status of this.state.statuses){
            params.status = status;
            let response = await api.get(
                url,
                params
            );
            opportunities = opportunities.concat(response.data.cards)
        }
        this.setState({
            opportunities: opportunities,
        });
    }

    async retrievePayments() {
        this.setState(
            {
                payments: null,
            }
        );
        const url = 'payment/';
        const api = new BaseApi();
        let params = {
            page_size: 10000,
            year: this.year,
        }
        let response = await api.get(url, params);
        this.setState(
            {
                payments: response.data.results,
            }
        );
    }
    async retrieveCertificates(callback) {
        this.setState(
            {
                certificates: null,
            }
        );
        const url = 'certificate/';
        const api = new BaseApi();
        let params = {
            page_size: 10000,
            year: this.year,
        }
        let response = await api.get(url, params);
        this.setState(
            {
                certificates: response.data.results,
            },
            async () => {
                if (callback){
                    await callback()
                }
            }
        );
    }

    async addSalary(data) {
        let config = this.state.config;
        let salaries = [];
        for (let i = 0; i < 12; i++) {
            if (i === 11){
                salaries.push(
                    {
                        date: (new Date(this.year, i, 25)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value / 2,
                    }
                )
                salaries.push(
                    {
                        date: (new Date(this.year, i, 28)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value / 2,
                    }
                )
            }
            else {
                salaries.push(
                    {
                        date: (new Date(this.year, i, 25)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value / 2,
                    }
                )
                salaries.push(
                    {
                        date: (new Date(this.year, i+1, 10)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value / 2,
                    }
                )
            }

        }
        data.active = true;
        let id;
        if (data.id){
            id = data.id
        }
        else {
            id = crypto.randomUUID();
            if (id in config.salaries){
                id = crypto.randomUUID();
            }
        }
        config.salaries[id] = {
            config: data,
            salaries: salaries,
        }
        await this.updateConfig(config);
    }

    async changeSalaryActive(id){
        let config = this.state.config;
        config.salaries[id].config.active = !config.salaries[id].config.active
        await this.updateConfig(config);
    }

    async deleteSalary(id){
        let config = this.state.config;
        delete config.salaries[id]
        await this.updateConfig(config);
    }

    async addAdminSalary(data) {
        let config = this.state.config;
        let salaries = [];
        for (let i = 0; i < 12; i++) {
            if (i === 11){
                salaries.push(
                    {
                        date: (new Date(this.year, i, 25)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value / 2,
                    }
                )
                salaries.push(
                    {
                        date: (new Date(this.year, i, 28)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value / 2,
                    }
                )
            }
            else {
                salaries.push(
                    {
                        date: (new Date(this.year, i, 25)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value / 2,
                    }
                )
                salaries.push(
                    {
                        date: (new Date(this.year, i+1, 10)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value / 2,
                    }
                )
            }

        }
        data.active = true;
        let id;
        if (data.id){
            id = data.id
        }
        else {
            id = crypto.randomUUID();
            if (id in config.adminSalaries){
                id = crypto.randomUUID();
            }
        }
        config.adminSalaries[id] = {
            config: data,
            salaries: salaries,
        }
        await this.updateConfig(config);
    }

    async changeAdminSalaryActive(id){
        let config = this.state.config;
        config.adminSalaries[id].config.active = !config.adminSalaries[id].config.active
        await this.updateConfig(config);
    }

    async deleteAdminSalary(id){
        let config = this.state.config;
        delete config.adminSalaries[id]
        await this.updateConfig(config);
    }

    async addPeriodExpense(data){
        let config = this.state.config;
        let expenses = [];
        if (data.period === 'month'){
            for (let i = 0; i < 12; i++){
                expenses.push(
                    {
                        date: (new Date(this.year, i, data.day)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value,
                    }
                )
            }
        }
        else if (data.period === 'quarter'){
            for (let i = 0; i < 12; i+=3){
                expenses.push(
                    {
                        date: (new Date(this.year, i, data.day)).toLocaleDateString('en-US'),
                        count: data.count,
                        value: data.value,
                    }
                )
            }
        }
        data.active = true;
        let id;
        if (data.id){
            id = data.id
        }
        else {
            id = crypto.randomUUID();
            if (id in config.periodExpenses){
                id = crypto.randomUUID();
            }
        }
        config.periodExpenses[id] = {
            config: data,
            expenses: expenses,
        }
        await this.updateConfig(config);
    }

    async changePeriodExpenseActive(id){
        let config = this.state.config;
        config.periodExpenses[id].config.active = !config.periodExpenses[id].config.active
        await this.updateConfig(config);
    }

    async deletePeriodExpense(id){
        let config = this.state.config;
        delete config.periodExpenses[id]
        await this.updateConfig(config);
    }

    async addOnceExpense(data){
        let config = this.state.config;
        data.active = true;
        let id;
        if (data.id){
            id = data.id
        }
        else {
            id = crypto.randomUUID();
            if (id in config.onceExpenses){
                id = crypto.randomUUID();
            }
        }
        config.onceExpenses[id] = {
            config: data,
            expense: {
                date: dayjs(data.date).toDate().toLocaleDateString('en-US'),
                count: 1,
                value: data.value,
            },
        }
        await this.updateConfig(config);
    }

    async changeOnceExpenseActive(id){
        let config = this.state.config;
        config.onceExpenses[id].config.active = !config.onceExpenses[id].config.active
        await this.updateConfig(config);
    }

    async deleteOnceExpense(id){
        let config = this.state.config;
        delete config.onceExpenses[id]
        await this.updateConfig(config);
    }

    async processOpportunityFilter(form) {
        await this.setState({
            statuses: [],
            xtypes: [],
        })
        let formDataObj = Object.fromEntries(form.state.formData.entries());
        await this.setState({
            statuses: formDataObj.statuses.split(','),
            xtypes: formDataObj.xtypes.split(','),
        });
        await this.retrieveOpportunities();
    }

    async preRefresh(){
        this.setState({
            needRefresh: true
        })
    }

    async refresh(){
        this.setState({
            ready: false,
        })
        await this.retrievePayments();
        await this.retrieveCertificates(
            this.calculateReport.bind(this)
        );
    }

    async updateStartCash(data){
        let config = this.state.config;
        config.startCash = parseInt(data.startCash);
        await this.updateConfig(config);
    }

    async updateTypicalContractFee(data){
        let config = this.state.config;
        config.typicalContractFee = parseInt(data.typicalContractFee);
        await this.updateConfig(config);
    }

    async updateProfitPercent(data){
        let config = this.state.config;
        config.profitPercent = parseInt(data.profitPercent);
        await this.updateConfig(config);
    }

    async updateWorkPercent(data){
        let config = this.state.config;
        config.workPercent = parseInt(data.workPercent);
        await this.updateConfig(config);
    }

    async calculateReport() {
        this.setState({
            ready: false,
        })
        let url;
        let params;
        let response;
        const api = new BaseApi();
        let report = {};
        let startDate = new Date(this.year, 0, 1)
        let endDate = new Date(this.year,11, 31)
        let dates = getDatesInRange(startDate, endDate)
        for (let date of dates) {
            report[date.toLocaleDateString('en-US')] = {
                incomeFact: 0,
                incomeForecast: 0,
                incomeLeadTail: 0,
                revenueFact: 0,
                revenueForecast: 0,
                revenueLeadTail: 0,
                expense: 0,
                salaries: 0,
                adminSalaries: 0,
                tax: 0,
                taxForecast: 0,
                workDays: 0,
                balance: 0,
                cash: 0,
                date: date.toLocaleDateString('en-US'),
            }
        }
        for (let e of Object.entries(this.state.config.onceExpenses)) {
            if (e[1].config.active){
                report[e[1].expense.date].expense += e[1].expense.value * e[1].expense.count
            }
        }
        for (let es of Object.entries(this.state.config.periodExpenses)) {
            if (es[1].config.active) {
                for (let e of es[1].expenses) {
                    report[e.date].expense += e.value * e.count
                }
            }
        }
        for (let es of Object.entries(this.state.config.salaries)) {
            if (es[1].config.active) {
                for (let e of es[1].salaries) {
                    report[e.date].salaries += e.value * 1.3 * 1.2 * e.count
                    report[e.date].workDays += 10 * e.count
                }
            }
        }
        for (let es of Object.entries(this.state.config.adminSalaries)) {
            if (es[1].config.active) {
                for (let e of es[1].salaries) {
                    report[e.date].adminSalaries += e.value * 1.3 * 1.3 * e.count
                }
            }
        }
        for (let i of this.state.payments){
            report[dayjs(i.date).toDate().toLocaleDateString('en-US')].incomeFact += i.revenue * i.forecast_percent / 100
            report[dayjs(i.date).toDate().toLocaleDateString('en-US')].tax += i.revenue * i.forecast_percent / 100 * 0.11
        }
        for (let r of this.state.certificates){
            report[dayjs(r.date).toDate().toLocaleDateString('en-US')].revenueFact += r.revenue * r.forecast_percent / 100
        }
        url = 'opportunity/forecast';
        params = {
            year: this.year,
        }
        response = await api.get(url, params);
        for (let r of response.data.by_start_date.revenue){
            report[dayjs(r.date).toDate().toLocaleDateString('en-US')].revenueForecast += r.value
        }
        for (let r of response.data.by_start_date.income){
            report[dayjs(r.date).toDate().toLocaleDateString('en-US')].incomeForecast += r.value
            report[dayjs(r.date).toDate().toLocaleDateString('en-US')].taxForecast += r.value * 0.11
        }
        for (let r of response.data.by_lead_tail.revenue){
            report[dayjs(r.date).toDate().toLocaleDateString('en-US')].revenueLeadTail += r.value
        }
        for (let r of response.data.by_lead_tail.income){
            report[dayjs(r.date).toDate().toLocaleDateString('en-US')].incomeLeadTail += r.value
            report[dayjs(r.date).toDate().toLocaleDateString('en-US')].taxForecast += r.value * 0.11
        }
        let reportList = []
        for (let day of Object.entries(report)){
            let _day = day[1]
            _day.trueDate = dayjs(_day.date).toDate()
            reportList.push(
                _day
            )
        }
        reportList.sort(
            function(a, b) {
                return compareDates(a, b)
            }
        )
        let prevCash = this.state.config.startCash;
        let prevCashForecast = this.state.config.startCash;
        for (let day of reportList){
            day.balanceForecast = day.incomeFact + day.incomeForecast + day.incomeLeadTail - day.expense - day.salaries - day.adminSalaries - day.tax - day.taxForecast;
            day.balance = day.incomeFact - day.expense - day.salaries - day.tax;
            day.cashForecast = prevCashForecast + day.balanceForecast;
            day.cash = prevCash + day.balance;
            prevCash = day.cash;
            prevCashForecast = day.cashForecast;
        }
        let [total, quarters] = calculateTotals(
            reportList,
            'incomeFact',
            'incomeForecast',
            'incomeLeadTail',
            'revenueFact',
            'revenueForecast',
            'revenueLeadTail',
            ['expense', 'adminSalaries'],
            ['salaries', ],
            'tax',
            'taxForecast',
            'balance',
            'balanceForecast',
            ['adminSalaries', ],
        )
        total.workDays = 0;
        for (let day of reportList){
            total.workDays += day.workDays;
        }
        total.cash = this.state.config.startCash + total.balance;
        total.expectedRevenue = (100 + this.state.config.profitPercent) / 100 * (total.expense + total.salaries) * (100 + 5) / 100 * (100 + 3) / 100;
        total.expectedDayRate = total.expectedRevenue / total.workDays / this.state.config.workPercent * 100;

        total.expensePercent = total.expense / total.salaries * 100;
        total.expectedEngineerDayRate = total.salaries / total.workDays / this.state.config.workPercent * 100;

        let config = this.state.config
        config.expensePercent = total.expensePercent
        config.expectedEngineerDayRate = total.expectedEngineerDayRate
        await this.postConfig(config);

        let forecast_revenues = response.data.by_start_date.revenue;
        forecast_revenues.sort(
            function(a, b) {
                return compareDates(a, b)
            }
        )
        let forecast_incomes = response.data.by_start_date.income;
        forecast_incomes.sort(
            function(a, b) {
                return compareDates(a, b)
            }
        )
        this.setState({
            forecast_revenues: forecast_revenues,
            forecast_incomes: forecast_incomes,
            report: reportList,
            total: total,
            quarters: quarters,
            ready: true,
        });
    }

    async updateConfig(config){
        this.setState(
            {
                config: config
            }
        );
        await this.postConfig(config);
        await this.calculateReport(config);
    }

    render() {
        let numberFormat = new Intl.NumberFormat('ru', {maximumFractionDigits: 2})
        if (!this.state.ready) {
            return (
                <CircularProgress/>
            )
        }
        return (
            <Stack
                spacing={2}
                sx={{
                    width: '100%',
                }}
            >
                <Stack
                    direction={'row'}
                    spacing={2}
                >
                    <Typography
                        level="h3"
                    >
                        {this.state.title?this.state.title:'Без названия'}
                    </Typography>
                    {
                        this.state.needRefresh?
                            <Button
                                size={'sm'}
                                color={'success'}
                                onClick={this.refresh.bind(this)}
                                startDecorator={<RefreshIcon size={'sm'}/> }
                            >
                                Обновить
                            </Button>:
                            null
                    }
                </Stack>
                <Card>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid xs={12} lg={12}>
                                <Card>
                                    <CardContent>
                                        <FinancialTotals
                                            total={this.state.total}
                                            quarters={this.state.quarters}
                                            miles={true}
                                        />

                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid xs={12} lg={12}>
                                <Card>
                                    <CardContent>
                                        <Typography
                                            level={'h4'}
                                            sx={{
                                                mb: 2
                                            }}
                                        >
                                            Модельные показатели
                                        </Typography>
                                        <Stack
                                            spacing={2}
                                        >
                                            <Stack
                                                direction={'row'}
                                                spacing={1}
                                                sx={{
                                                    alignItems: "center",
                                                    mb: 1,
                                                }}
                                            >
                                                <Card
                                                    color="lime"
                                                    size={'sm'}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            level={'body-xs'}
                                                        >
                                                            Процент прибыли
                                                        </Typography>
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                        >
                                                            <Typography>
                                                                {numberFormat.format(this.state.config.profitPercent)}
                                                            </Typography>
                                                            <FinancialModelConfigValueUpdate
                                                                overlay={true}
                                                                name={'profitPercent'}
                                                                title={'Процент прибыли'}
                                                                defaultValue={this.state.config.profitPercent}
                                                                callBack={this.updateProfitPercent.bind(this)}
                                                            />
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                                <Card
                                                    color="lime"
                                                    size={'sm'}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            level={'body-xs'}
                                                        >
                                                            Процент загрузки
                                                        </Typography>
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                        >
                                                            <Typography>
                                                                {numberFormat.format(this.state.config.workPercent)}
                                                            </Typography>
                                                            <FinancialModelConfigValueUpdate
                                                                overlay={true}
                                                                name={'workPercent'}
                                                                title={'Процент загрузки'}
                                                                defaultValue={this.state.config.workPercent}
                                                                callBack={this.updateWorkPercent.bind(this)}
                                                            />
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                                <Card
                                                    color="lime"
                                                    size={'sm'}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            level={'body-xs'}
                                                        >
                                                            Типичная стоимость сделки
                                                        </Typography>
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                        >
                                                            <Typography>
                                                                {numberFormat.format(this.state.config.typicalContractFee)}
                                                            </Typography>
                                                            <FinancialModelConfigValueUpdate
                                                                overlay={true}
                                                                name={'typicalContractFee'}
                                                                title={'Типичная стоимость сделки'}
                                                                defaultValue={this.state.config.typicalContractFee}
                                                                callBack={this.updateTypicalContractFee.bind(this)}
                                                            />
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            </Stack>
                                            <Stack
                                                direction={'row'}
                                                spacing={1}
                                                sx={{
                                                    alignItems: "center",
                                                    mb: 1,
                                                }}
                                            >
                                                <Card
                                                    color="lime"
                                                    size={'sm'}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            level={'body-xs'}
                                                        >
                                                            Цена человеко-дня
                                                        </Typography>
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                        >
                                                            <Typography>
                                                                {numberFormat.format(this.state.total.expectedDayRate)}
                                                            </Typography>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                                <Typography
                                                    level={'h4'}
                                                >
                                                   =
                                                </Typography>
                                                <Card
                                                    color="lime"
                                                    size={'sm'}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            level={'body-xs'}
                                                        >
                                                            Искомая выручка
                                                        </Typography>
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                        >
                                                            <Typography>
                                                                {numberFormat.format(this.state.total.expectedRevenue)}
                                                            </Typography>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                                <Typography
                                                    level={'h4'}
                                                >
                                                   /
                                                </Typography>
                                                <Card
                                                    color="lime"
                                                    size={'sm'}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            level={'body-xs'}
                                                        >
                                                            Доступная загрузка
                                                        </Typography>
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                        >
                                                            <Typography>
                                                                {numberFormat.format(this.state.total.workDays * this.state.config.workPercent / 100)} дн. ({numberFormat.format(this.state.total.workDays * 8 * this.state.config.workPercent / 100)} ч.)
                                                            </Typography>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            </Stack>
                                            <Stack
                                                direction={'row'}
                                                spacing={2}
                                            >
                                                <Card
                                                    color="lime"
                                                    size={'sm'}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            level={'body-xs'}
                                                        >
                                                            Стоимость дня инженера
                                                        </Typography>
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                        >
                                                            <Typography>
                                                                {
                                                                    numberFormat.format(
                                                                        this.state.total.expectedEngineerDayRate
                                                                    )
                                                                }
                                                            </Typography>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                                <Card
                                                    color="lime"
                                                    size={'sm'}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            level={'body-xs'}
                                                        >
                                                            Процент накладных
                                                        </Typography>
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                        >
                                                            <Typography>
                                                                {
                                                                    numberFormat.format(
                                                                    this.state.total.expensePercent
                                                                    )
                                                                } %
                                                            </Typography>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <Card>
                    <CommonPaginatedTable
                        title={'Административные работники'}
                        data={Object.keys(this.state.config.adminSalaries).map(
                            (key) => {
                                let value = {
                                    id: key,
                                    ...this.state.config.adminSalaries[key].config
                                }
                                for (let xkey of Object.keys(value)){
                                    value[xkey] = value[xkey] * 1?value[xkey] * 1:value[xkey]
                                }
                                return value
                            }
                        )}
                        sortingFields={[
                            'title',
                            'value',
                        ]}
                        fields={[
                            {
                                title: 'Название',
                                name: 'title',
                                visible: true,
                            },
                            {
                                title: 'Количество',
                                name: 'count',
                                visible: true,
                            },
                            {
                                title: 'Оклад в договор',
                                name: 'value',
                                visible: true,
                            },
                            {
                                title: '',
                                visible: true,
                                component: FinancialModelSalaryCreate,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    buttonIcon: EditIcon,
                                    buttonLabel: 'Редактировать',
                                    submitLabel: 'Сохранить',
                                },
                                callBack: this.addAdminSalary.bind(this),
                            },
                            {
                                title: 'Активно',
                                name: 'active',
                                visible: true,
                            },
                            {
                                title: '',
                                visible: true,
                                component: CommonCallBackButton,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    propsObjectKey: 'id',
                                    icon: ChangeCircleIcon,
                                },
                                callBack: this.changeAdminSalaryActive.bind(this),
                            },
                            {
                                title: '',
                                visible: true,
                                component: CommonCallBackButton,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    propsObjectKey: 'id',
                                    icon: DeleteIcon,
                                },
                                callBack: this.deleteAdminSalary.bind(this),
                            },
                        ]}
                        extraComponents={[
                            {
                                component: FinancialModelSalaryCreate,
                                callBack: this.addAdminSalary.bind(this),
                            }
                        ]}
                    />
                    <CommonPaginatedTable
                        title={'Инженерные работники'}
                        data={Object.keys(this.state.config.salaries).map(
                            (key) => {
                                let value = {
                                    id: key,
                                    ...this.state.config.salaries[key].config
                                }
                                for (let xkey of Object.keys(value)){
                                    value[xkey] = value[xkey] * 1?value[xkey] * 1:value[xkey]
                                }
                                return value
                            }
                        )}
                        sortingFields={[
                            'title',
                            'value',
                        ]}
                        fields={[
                            {
                                title: 'Название',
                                name: 'title',
                                visible: true,
                            },
                            {
                                title: 'Количество',
                                name: 'count',
                                visible: true,
                            },
                            {
                                title: 'Оклад в договор',
                                name: 'value',
                                visible: true,
                            },
                            {
                                title: '',
                                visible: true,
                                component: FinancialModelSalaryCreate,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    buttonIcon: EditIcon,
                                    buttonLabel: 'Редактировать',
                                    submitLabel: 'Сохранить',
                                },
                                callBack: this.addSalary.bind(this),
                            },
                            {
                                title: 'Активно',
                                name: 'active',
                                visible: true,
                            },
                            {
                                title: '',
                                visible: true,
                                component: CommonCallBackButton,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    propsObjectKey: 'id',
                                    icon: ChangeCircleIcon,
                                },
                                callBack: this.changeSalaryActive.bind(this),
                            },
                            {
                                title: '',
                                visible: true,
                                component: CommonCallBackButton,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    propsObjectKey: 'id',
                                    icon: DeleteIcon,
                                },
                                callBack: this.deleteSalary.bind(this),
                            },
                        ]}
                        extraComponents={[
                            {
                                component: FinancialModelSalaryCreate,
                                callBack: this.addSalary.bind(this),
                            }
                        ]}
                    />
                    <CommonPaginatedTable
                        title={'Периодические траты'}
                        data={Object.keys(this.state.config.periodExpenses).map(
                            (key) => {
                                let value = {
                                    id: key,
                                    ...this.state.config.periodExpenses[key].config
                                }
                                for (let xkey of Object.keys(value)){
                                    value[xkey] = value[xkey] * 1?value[xkey] * 1:value[xkey]
                                }
                                return value
                            }
                        )}
                        sortingFields={[
                            'title',
                            'value',
                        ]}
                        fields={[
                            {
                                title: 'Название',
                                name: 'title',
                                visible: true,
                            },
                            {
                                title: 'Период',
                                name: 'period',
                                visible: true,
                            },
                            {
                                title: 'День выплаты',
                                name: 'day',
                                visible: true,
                            },
                            {
                                title: 'Количество',
                                name: 'count',
                                visible: true,
                            },
                            {
                                title: 'Значение',
                                name: 'value',
                                visible: true,
                            },
                            {
                                title: '',
                                visible: true,
                                component: FinancialModelPeriodExpenseCreate,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    buttonIcon: EditIcon,
                                    buttonLabel: 'Редактировать',
                                    submitLabel: 'Сохранить',
                                },
                                callBack: this.addPeriodExpense.bind(this),
                            },
                            {
                                title: 'Активно',
                                name: 'active',
                                visible: true,
                            },
                            {
                                title: '',
                                visible: true,
                                component: CommonCallBackButton,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    propsObjectKey: 'id',
                                    icon: ChangeCircleIcon,
                                },
                                callBack: this.changePeriodExpenseActive.bind(this),
                            },
                            {
                                title: '',
                                visible: true,
                                component: CommonCallBackButton,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    propsObjectKey: 'id',
                                    icon: DeleteIcon,
                                },
                                callBack: this.deletePeriodExpense.bind(this),
                            },
                        ]}
                        extraComponents={[
                            {
                                component: FinancialModelPeriodExpenseCreate,
                                callBack: this.addPeriodExpense.bind(this),
                            }
                        ]}
                    />
                    <CommonPaginatedTable
                        title={'Разовые траты'}
                        data={Object.keys(this.state.config.onceExpenses).map(
                            (key) => {
                                let value = {
                                    id: key,
                                    ...this.state.config.onceExpenses[key].config
                                }
                                for (let xkey of Object.keys(value)){
                                    value[xkey] = value[xkey] * 1?value[xkey] * 1:value[xkey]
                                }
                                return value
                            }
                        )}
                        sortingFields={[
                            'title',
                            'value',
                        ]}
                        fields={[
                            {
                                title: 'Название',
                                name: 'title',
                                visible: true,
                            },
                            {
                                title: 'Дата',
                                name: 'date',
                                visible: true,
                            },
                            {
                                title: 'Значение',
                                name: 'value',
                                visible: true,
                            },
                            {
                                title: '',
                                visible: true,
                                component: FinancialModelOnceExpenseCreate,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    buttonIcon: EditIcon,
                                    buttonLabel: 'Редактировать',
                                    submitLabel: 'Сохранить',
                                },
                                callBack: this.addOnceExpense.bind(this),
                            },
                            {
                                title: 'Активно',
                                name: 'active',
                                visible: true,
                            },

                            {
                                title: '',
                                visible: true,
                                component: CommonCallBackButton,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    propsObjectKey: 'id',
                                    icon: ChangeCircleIcon,
                                },
                                callBack: this.changeOnceExpenseActive.bind(this),
                            },
                            {
                                title: '',
                                visible: true,
                                component: CommonCallBackButton,
                                componentProps: {
                                    flat: true,
                                    propsObject: 'componentObj',
                                    propsObjectKey: 'id',
                                    icon: DeleteIcon,
                                },
                                callBack: this.deleteOnceExpense.bind(this),
                            },
                        ]}
                        extraComponents={[
                            {
                                component: FinancialModelOnceExpenseCreate,
                                callBack: this.addOnceExpense.bind(this),
                            }
                        ]}
                    />
                </Card>
                <Stack
                    direction={'row'}
                    spacing={2}
                >
                    <Card
                        sx={{
                            height: '300px',
                            overflowY: 'scroll',
                        }}
                    >
                        <CardContent
                        >
                            <CommonPaginatedTable
                                title={'Входящие платежи'}
                                data={this.state.payments}
                                url={'payment'}
                                fields={[
                                    {
                                        title: 'Дата',
                                        name: 'date',
                                        visible: true,
                                        preProcess: (value) => (value?dayjs(value).format('DD MMM YYYY'):value)
                                    },
                                    {
                                        title: 'Сделка',
                                        name: 'opportunity.short_name',
                                        visible: true,
                                        linkPattern: '/opportunity/detail/:linkParam',
                                        linkParam: 'opportunity.id',
                                        linkTarget: '_blank'
                                    },
                                    {
                                        title: 'Сумма',
                                        name: 'revenue',
                                        visible: true,
                                    },
                                    {
                                        title: 'Процент в прогнозе',
                                        name: 'forecast_percent',
                                        visible: true,
                                        edit: {
                                            permission: true,
                                            fields: ['forecast_percent', ],
                                            baseUrl: 'payment',
                                            callBack: this.preRefresh.bind(this),
                                        },
                                    },
                                ]}
                            />
                        </CardContent>
                    </Card>
                    <Card
                        sx={{
                            height: '300px',
                            overflowY: 'scroll',
                        }}
                    >
                        <CardContent
                        >
                            <CommonPaginatedTable
                                title={'Акты'}
                                data={this.state.certificates}
                                url={'certificate'}
                                fields={[
                                    {
                                        title: 'Дата',
                                        name: 'date',
                                        visible: true,
                                        preProcess: (value) => (value?dayjs(value).format('DD MMM YYYY'):value),
                                    },
                                    {
                                        title: 'Сделка',
                                        name: 'opportunity.short_name',
                                        visible: true,
                                        linkPattern: '/opportunity/detail/:linkParam',
                                        linkParam: 'opportunity.id',
                                        linkTarget: '_blank'
                                    },
                                    {
                                        title: 'Сумма',
                                        name: 'revenue',
                                        visible: true,
                                    },
                                    {
                                        title: 'Процентр в прогнозе',
                                        name: 'forecast_percent',
                                        visible: true,
                                        edit: {
                                            permission: true,
                                            fields: ['forecast_percent', ],
                                            baseUrl: 'certificate',
                                            callBack: this.preRefresh.bind(this),
                                        },
                                    },
                                ]}
                            />
                        </CardContent>
                    </Card>
                </Stack>
                <Stack
                    direction={'row'}
                    spacing={2}
                >
                    <Card
                        sx={{
                            height: '300px',
                            overflowY: 'scroll',
                        }}
                    >
                        <CardContent
                        >
                            <CommonPaginatedTable
                                title={'Входящие платежи (прогноз)'}
                                data={this.state.forecast_incomes}
                                url={'payment'}
                                fields={[
                                    {
                                        title: 'Дата',
                                        name: 'date',
                                        visible: true,
                                        preProcess: (value) => (value?dayjs(value).format('DD MMM YYYY'):value)
                                    },
                                    {
                                        title: 'Сделка',
                                        name: 'opportunity.short_name',
                                        visible: true,
                                        linkPattern: '/opportunity/detail/:linkParam',
                                        linkParam: 'opportunity.id',
                                        linkTarget: '_blank'
                                    },
                                    {
                                        title: 'Сумма',
                                        name: 'value',
                                        visible: true,
                                    },

                                ]}
                            />
                        </CardContent>
                    </Card>
                    <Card
                        sx={{
                            height: '300px',
                            overflowY: 'scroll',
                        }}
                    >
                        <CardContent
                        >
                            <CommonPaginatedTable
                                title={'Акты (прогноз)'}
                                data={this.state.forecast_revenues}
                                url={'certificate'}
                                fields={[
                                    {
                                        title: 'Дата',
                                        name: 'date',
                                        visible: true,
                                        preProcess: (value) => (value?dayjs(value).format('DD MMM YYYY'):value)
                                    },
                                    {
                                        title: 'Сделка',
                                        name: 'opportunity.short_name',
                                        visible: true,
                                        linkPattern: '/opportunity/detail/:linkParam',
                                        linkParam: 'opportunity.id',
                                        linkTarget: '_blank'
                                    },
                                    {
                                        title: 'Сумма',
                                        name: 'value',
                                        visible: true,
                                    },
                                ]}
                            />
                        </CardContent>
                    </Card>
                </Stack>
                <Card>
                    <Typography
                        level={'h4'}
                    >
                        Сделки в прогнозе
                    </Typography>
                    <CommonForm
                        processForm={this.processOpportunityFilter.bind(this)}
                        fields={[
                            {
                                name: 'statuses',
                                label: 'Статусы сделки',
                                xs: 12,
                                sm: null,
                                md: 6,
                                lg: null,
                                xl: null,
                                required: true,
                                default: ['OPENX',],
                                type: 'multiselect',
                                options: [
                                    {'value': 'OPENX', 'label': 'В работе'},
                                    {'value': 'DELAY', 'label': 'Ожидает'},
                                    {'value': 'CLOSE', 'label': 'Закрыта'},
                                    {'value': 'LOSTX', 'label': 'Потеряна'},
                                ]
                            },
                            {
                                'name': 'xtypes',
                                'label': 'Типы процессов',
                                'xs': 12,
                                'sm': null,
                                'md': 6,
                                'lg': null,
                                'xl': null,
                                'required': false,
                                'default': [],
                                'type': 'multiselect',
                                'options': this.state.xtypes_choices.map((each) => {return(
                                    {
                                        'value': each[0],
                                        'label': each[1]
                                    }
                                )})
                            },
                            {
                                'type': 'submit',
                                'label': 'Отобрать'
                            }
                        ]}
                    />
                    <Box
                        sx={{
                            height: '600px',
                            overflowY: 'scroll',
                        }}
                    >
                        <CommonPaginatedTable
                            title={''}
                            data={this.state.opportunities}
                            fields={[
                                {
                                    title: 'Название',
                                    name: 'opportunity.short_name',
                                    visible: true,
                                    linkPattern: '/opportunity/detail/:linkParam',
                                    linkParam: 'opportunity.id',
                                    linkTarget: '_blank'
                                },
                                {
                                    title: 'Компания',
                                    name: 'opportunity.account.nameshort',
                                    visible: true,
                                    linkPattern: '/account/detail/:linkParam',
                                    linkParam: 'opportunity.account.id',
                                    linkTarget: '_blank'
                                },
                                {
                                    title: 'Карточка',
                                    visible: true,
                                    component: FinancialModelOpportunityCardWrapper,
                                    // callBack: this.calculateReport.bind(this)
                                },
                                {
                                    title: 'Текущее ТКП - цена',
                                    name: 'opportunity.current_quote.price',
                                    visible: true,
                                },
                                {
                                    title: 'Текущее ТКП - рабочие дни',
                                    name: 'opportunity.current_quote.work_days',
                                    visible: true,
                                },
                                {
                                    title: 'Параметры в прогнозе',
                                    visible: true,
                                    component: FinancialModelForecastParameters,
                                    callBack: this.setOpportunityValue.bind(this),
                                    width: '40%',
                                },
                                {
                                    title: 'Прогнозная дата начала',
                                    visible: true,
                                    name: 'opportunity.forecast_start_date',
                                    preProcess: (value) => (value?dayjs(value).format('DD MMM YYYY'):value),
                                    edit: {
                                        permission: true,
                                        fields: ['forecast_start_date', ],
                                        baseUrl: 'opportunity',
                                        obj: 'opportunity',
                                        callBack: this.setOpportunityValue.bind(this),
                                    },
                                    width: '20%',
                                },
                            ]}
                        />
                    </Box>
                </Card>
            </Stack>
        )
    }
}

export default withRouter(FinancialModel);